<template>
  <transition name="drawer-slide-left">
    <div
      v-if="isOpen"
      :ref="setDrawerRef"
      class="drawer"
    >
      <div class="drawer-header">
        <div class="flex items-center w-full">
          <h1 class="text-xl font-medium grow-1">
            <span
              v-if="isLoading"
              class="skeleton h-5 w-auto"
            />
            <span data-sentry-unmask>
              <slot name="heading">
                {{ heading }}
              </slot>
            </span>
          </h1>
          <close-button
            class="justify-self-end"
            @close="close"
          />
        </div>
      </div>
      <div class="drawer-body-wrapper">
        <div class="drawer-body">
          <slot name="body" />
        </div>
        <div
          v-if="$slots.footer"
          class="drawer-footer"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import CloseButton from '@/components/ui/CloseButton.vue';
import { watch, nextTick, onUnmounted, onMounted } from 'vue';

defineOptions({
  name: 'AppDrawer',
});

const props = defineProps({
  drawerRef: {
    type: String,
    required: true,
  },
  isOpen: {
    type: Boolean,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  heading: {
    type: String,
    required: false, // Kept false because the slot can replace it
  },
  clickOutsideAllowClasses: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);
const close = () => {
  emit('close');
};

/**
 * Assigns a DOM element to the refsMap using the drawer's reference name.
 * This function is intended to be used as a Vue ref callback.
 *
 * @param {HTMLElement|null} el - The DOM element associated with the drawer, or null when unmounted.
 */
const refsMap = {};
function setDrawerRef(el) {
  refsMap[props.drawerRef] = el;
}

/**
 * Handles click events to determine if the drawer should close.
 *
 * - Uses the event's composed path to detect if the click originated outside the drawer,
 *   even if interactive overlays (such as datepicker popovers) are rendered outside the drawer's DOM subtree.
 * - Respects allow-listed elements (by class) anywhere in the event path that should not trigger closure.
 *
 * @param {MouseEvent} event - The native browser click event.
 * @emits close - When the click meets closure criteria (outside drawer and not in the allow-list).
 * @listens click - On window/document for detecting outside interactions.
 *
 * @example Logic Flow
 * 1. Early exit if drawer is closed or missing.
 * 2. Check if click is outside drawer
 * 3. Obtain the full event path using event.composedPath().
 * 4. Check if any element in the path is the drawer or an allow-listed element.
 * 5. Emit close if the click is outside the drawer and not in the allow-list.
 */
const handleClickOutside = (event) => {
  const drawerElement = refsMap[props.drawerRef];

  // Early exit if drawer isn't open or element missing
  if (!drawerElement || !props.isOpen) return;

  // Check if click is outside first
  const isClickOutside = !drawerElement.contains(event.target);
  if (!isClickOutside) return;

  // Check if click was performed within an allowed element
  const allowedClasses = [
    ...props.clickOutsideAllowClasses,
    'alert-close-btn',
    'drawer-body',
    'modal',
    'toast-content',
  ];

  // Get ALL elements in the click path (even if some were removed)
  const clickPath = event.composedPath();

  // Check if any element in the path is inside the modal or allowed elements
  const isClickInAllowedElement = clickPath.some((element) => {
    return element.classList && allowedClasses.some((className) => element.classList.contains(className));
  });

  if (!isClickInAllowedElement) {
    close();
  }
};

/**
 * Manages the click-outside event listener based on the drawer's open state.
 *
 * - When the drawer opens, attaches a click event listener to the window (after DOM updates)
 *   to detect and handle clicks outside the drawer.
 * - When the drawer closes, removes the click event listener to prevent unnecessary checks.
 *
 * @param {boolean} isOpen - Indicates whether the drawer is open (true) or closed (false).
 *
 * @listens window:click - Detects clicks outside the drawer when open.
 */
const addOutsideClickListener = (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
    });
  } else {
    window.removeEventListener('click', handleClickOutside);
  }
};

onMounted(() => {
  addOutsideClickListener(props.isOpen);
});

watch(
  () => props.isOpen,
  (isOpen) => {
    addOutsideClickListener(isOpen);
  }
);

/**
 * Cleans up event listeners when component unmounts
 * @hook onUnmounted
 */
onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside);
});
</script>

<style>
@reference '@/assets/main.css';

.drawer {
  --drop-shadow: var(--drop-shadow-2xl);
  --width: var(--drawer-width);
  @apply flex flex-col fixed top-0 right-0 h-screen z-30 shadow-[var(--drop-shadow)] w-[var(--width)] bg-white border-l border-base-content/10;
}

.drawer-header {
  @apply p-6 sticky top-0 bg-white z-30;
}

.drawer-body-wrapper {
  @apply flex-grow overflow-y-auto h-full;
}

.drawer-body {
  @apply p-6 pt-0 min-h-full;
}

.drawer-footer {
  @apply border-t border-base-content/10 absolute bottom-0 left-0 right-0 p-3 flex gap-2 justify-end;
}
</style>
